import { Formik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import Modal from "react-modal";
import { useMutation } from "react-query";
import AGSelect from "../../../components/AgSelect";
import AGButton from "../../../components/Button";
import AGInput from "../../../components/Input";
import { StyleScreenWidthDefault, StyleScreenWidthLarger } from "../../../components/Modal/modal.styled";
import AGTextarea from "../../../components/Textarea";
import { OptionModel } from "../../../models";
import { UserContext } from "../../../Provider";
import { formSendMessageSchema } from "../../../Schema/CommerceCompassSchema";
import { httpGET, httpPOST } from "../../../utils/ajaxHelper";
import { MAILL_INFO } from "../../../utils/constant";
import { MESSAGE_NOTIFI } from "../../../utils/messages";
import { compare } from "../../../utils/sort";
import { IFormMessageModalProps, IFormSendMessage, InfoIndividual } from "./formMessageModal.props";


const RegionContactFormMessageModal: React.FC<IFormMessageModalProps> = (
    {
        sendTo,
        dataFirm,
        isShow = false,
        handleClose,
        handleShowNoti,
        isMultipleSendMail = false,
        pageType,
        regionId,
        ...props
    }: IFormMessageModalProps) => {
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);
    const user = useContext(UserContext);

    const [firmNotSend, setFirmNotSend] = useState<any | undefined>(undefined);
    const [optionServices, setOptionServices] = useState<OptionModel[]>([])
    const [selectOptionService, setSelectOptionService] = useState<OptionModel | undefined>(undefined)
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
            setScreenHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const customStyles = (screenWidth >= 769 && screenHeight > 430) ? StyleScreenWidthLarger(undefined, 700, true) : StyleScreenWidthDefault;

    const [formSendMessage, setFormSendMessage] = useState<IFormSendMessage>({
        sendTo: [],
        sendCC: MAILL_INFO.CC,
        subject: "",
        message: "",
        actionSend: sendTo
    });


    useEffect(() => {
        setFormSendMessage(e => {
            return {
                ...e,
                actionSend: sendTo
            }
        })
    }, [sendTo])

    const [infoIndividuals, setInfoIndividuals] = useState<InfoIndividual[]>([])
    const [firmContacts, setFirmContacts] = useState<InfoIndividual[]>([])
    const [serviceContacts, setServiceContacts] = useState<InfoIndividual[]>([])
    const [mainServicesFiltered, setMainServicesFiltered] = useState<InfoIndividual[]>([]);

    const { mutate: getRecipients } = useMutation(
        (ids: string) => httpGET({
            url: `/SendMessage/region-recipient?reqIds=${ids}&regionId=${regionId}&action=${sendTo}`,
        }),
        {
            onSuccess: (rs) => {
                if (rs && rs.length) {
                    setInfoIndividuals(rs)

                    const rsFirmIdSent = [...new Set(rs.map((e: any) => e.firmId))]
                    const dataFirmNotSend = dataFirm.filter(t => !rsFirmIdSent.includes(t.firmID)).map(e => e.firmName).join(", ")
                    const dataFirmSend = dataFirm.filter(t => rsFirmIdSent.includes(t.firmID))

                    const dataSendTo = {
                        "Firm": dataFirmSend.map(e => ({
                            firmId: e.firmID,
                            serviceIds: []
                        })),
                        "Service": dataFirm.map(d => ({
                            firmId: d.firmID,
                            serviceIds: d.services?.map(e => e.comOfferingId) || []
                        }))
                    }[sendTo]

                    setFormSendMessage({
                        ...formSendMessage,
                        sendTo: dataSendTo
                    })

                    setFirmNotSend(
                      dataFirmNotSend && dataFirmNotSend.length && sendTo === "Firm" ? (
                        <h1 className="py-[10px] px-[20px]">
                          The message was successfully sent to{" "}
                          <b>{dataFirmSend.map((e) => e.firmName).join(", ")}</b>, but could not be
                          sent to <b>{dataFirmNotSend}</b> due to the lack of contacts for the
                          organization. Please contact{" "}
                          {process.env.REACT_APP_CONTACT_NAME || "Natasha Schamberger"} at{" "}
                          <a
                            href={`mailto:${
                              process.env.REACT_APP_CONTACT_MAIL || "natasha@allinialglobal.com"
                            }`}
                            className="text-[#2c76aa]"
                          >
                            {process.env.REACT_APP_CONTACT_MAIL || "natasha@allinialglobal.com"}
                          </a>
                          / <i>{process.env.REACT_APP_CONTACT_PHONE || "+1470 567 4594"}</i> for
                          further information.
                        </h1>
                      ) : undefined,
                    );

                    return;
                }
                handleClose()
            },
        },
    );

    const { mutate: getFirmAndServiceRecipients } = useMutation(
        (ids: string) => httpGET({
            url: `/SendMessage/get-recipients?reqIds=${ids}`,
        }),
        {
            onSuccess: (rs) => {
                setServiceContacts(rs.serviceContacts)
                setFirmContacts(rs.firmContacts)
            },
        },
    );

    const { mutate: sendMessage } = useMutation(
        (data: IFormSendMessage) => httpPOST({
            url: `/SendMessage?regionId=${regionId}`,
            data
        }),
        {
            onSuccess: (rs) => {
                if (rs) {
                    handleClose();
                    handleShowNoti(firmNotSend);
                }
            },
            onError: (err) => {
                handleClose()
            }
        },
    );

    const { mutate: sendMessageMultiServicesAndFirms } = useMutation(
        (data: IFormSendMessage) => httpPOST({
            url: `/SendMessage/send-multi-services-firms?reqIds=${serviceContacts.map(x => x.serviceId).join(',')}`,
            data
        }),
        {
            onSuccess: (rs) => {
                if (rs) {
                    handleClose();
                    handleShowNoti(firmNotSend);
                    setSelectOptionService(undefined);
                }
            },
            onError: (err) => {
                handleClose();
                setSelectOptionService(undefined);
            }
        },
    );


    const getServices = () => {
        if (isMultipleSendMail) {
            const servicesData = dataFirm.map((d) => ({
                firmId: d.firmID,
                services: d.services?.map(e => ({
                    mainServiceName: e.comOfferingPracticeLevels ?
                        e.comOfferingPracticeLevels[0].fkSecondaryService?.fkMainOffering?.mainServiceName
                        : null,
                    mainServiceId: e.comOfferingPracticeLevels ?
                        e.comOfferingPracticeLevels[0].fkSecondaryService?.fkMainOffering?.mainServiceId
                        : null,
                    comServiceIds: e.comOfferingId
                })) ?? []
            }))

            let optiopnServices: any[] = [];

            servicesData.forEach((e, i) => {
                optiopnServices = [
                    ...optiopnServices,
                    ...e.services.filter(s => !optiopnServices.some(t => t.value === s.mainServiceId)).map(s => ({
                        value: s.mainServiceId,
                        label: s.mainServiceName,
                    }))
                ]
            })

            setOptionServices(optiopnServices.sort((a, b) => compare(a, b, "label")))

            if (optiopnServices.length === 1) {
                setSelectOptionService(optiopnServices[0]);
                filterServicesExist(optiopnServices[0].value);
                setFormSendMessage({
                    ...formSendMessage,
                });
            };
        }
    }

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const genRecipients = () => {
        return dataFirm.sort((a, b) => a.firmName?.localeCompare(b.firmName ?? "") ?? -1)
    }
    
    const filterServicesExist = (id: number) => {
        const servicesData = dataFirm.map((d) => ({
            firmId: d.firmID,
            services: d.services?.map(e => ({
                mainServiceName: e.comOfferingPracticeLevels ?
                    e.comOfferingPracticeLevels[0].fkSecondaryService?.fkMainOffering?.mainServiceName
                    : null,
                mainServiceId: e.comOfferingPracticeLevels ?
                    e.comOfferingPracticeLevels[0].fkSecondaryService?.fkMainOffering?.mainServiceId
                    : null,
                comServiceIds: e.comOfferingId
            })) ?? []
        }));

        const filterMainSerivce = servicesData.filter(x => x.services.some(y => y.mainServiceId === id));
        setMainServicesFiltered(filterMainSerivce as []);

        getFirmAndServiceRecipients(filterMainSerivce.map(e => e.services.filter(o => o.mainServiceId === id).map(i => i.comServiceIds).join(",")).join(","));

        return filterMainSerivce;
    }

    const getFirmName = (firmId: number) => {
        return dataFirm.find(x => x.firmID === firmId)?.firmName;
    }

    const getHelperDescription = () => {
        if (sendTo === "Firm") {
            return MESSAGE_NOTIFI.INQUIRY_FIRMS;
        } else {
            switch(pageType) {
                case "Service":
                    return MESSAGE_NOTIFI.INQUIRY_SERVICES;
                case "Product":
                    return MESSAGE_NOTIFI.INQUIRY_PRODUCTS;
                case "Secondment":
                    return MESSAGE_NOTIFI.INQUIRY_SECONDMENT;
                default:
                    return "";
            }
        }
    }

    if (!isShow) { return null; }

    return (
        <>
            <Modal
                isOpen={isShow}
                style={customStyles}
                closeTimeoutMS={100}
                ariaHideApp={false}
                onAfterOpen={() => {
                    getServices()
                    let ids = {
                        "Firm": dataFirm.map(d => d.firmID).join(","),
                        "Service": dataFirm.map((d) => d.services?.map(e => e.comOfferingId).join(",")).join(","),
                    }[sendTo]

                    getRecipients(ids)
                }}
                onAfterClose={() => {
                    setFormSendMessage({
                        message: "",
                        sendTo: [],
                        actionSend: sendTo,
                        sendCC: MAILL_INFO.CC,
                        subject: ""
                    })
                }}
            >
                <Formik
                    initialValues={{ ...formSendMessage }}
                    onSubmit={(values: any) => {
                        setFormSendMessage({
                            ...formSendMessage,
                            subject: values.subject,
                            message: values.message
                        })

                        var data = {
                            ...formSendMessage,
                            subject: values.subject,
                            message: values.message,
                            isSendMultiple: isMultipleSendMail
                        }

                        if (isMultipleSendMail && selectOptionService && sendTo === "Service") {
                            sendMessageMultiServicesAndFirms(data);
                        } else {
                            sendMessage(data)
                        }
                    }}
                    validationSchema={formSendMessageSchema}
                    validateOnBlur={false}
                >
                    {({ values, setFieldValue, errors, submitForm }) => {
                        return <>
                            <div className="p-[20px] text-[#58585B]">
                                <div className="innerBox">
                                    {
                                        isMultipleSendMail ? <div className="mb-[20px]" >
                                            <p>
                                                <b className="">
                                                    <span className="text-[#ae1515]">*</span> {getHelperDescription()}
                                                </b>
                                            </p>
                                            <p>
                                                <b className="">
                                                    <span className="text-[#ae1515]">*</span> {MESSAGE_NOTIFI.NOTE_EMAILL_CC}
                                                </b>
                                            </p>
                                        </div> : null
                                    }

                                    {
                                        !isMultipleSendMail ?
                                            <div className="mb-[20px]" >
                                                <p>
                                                    <b className="">
                                                        <span className="text-[#ae1515]">*</span> {MESSAGE_NOTIFI.NOTE_EMAILL_CC}
                                                    </b>
                                                </p>
                                            </div>
                                            : null
                                    }

                                    {
                                        isMultipleSendMail && sendTo === "Service" ?
                                            <div className="flex flex-row items-center mb-[20px] space-x-[20px]">
                                                <p>Services</p>
                                                <AGSelect
                                                    value={selectOptionService}
                                                    options={optionServices}
                                                    onChange={(options: any) => {
                                                        setSelectOptionService(options)

                                                        if (options && options.value) {
                                                            filterServicesExist(options.value);
                                                            setFormSendMessage({
                                                                ...formSendMessage,
                                                            })
                                                        }
                                                    }}
                                                    placeholder="Please select a service"
                                                    isClearable={true}
                                                />
                                            </div>
                                            : null
                                    }

                                    {
                                        isMultipleSendMail && sendTo === "Firm" ?
                                            infoIndividuals && genRecipients().map((t, index) =>
                                                <div className={`${index !== 0 && "mt-[20px]"}`}>
                                                    <p className="text-[1rem]  mb-[5px] font-bold">Recipient {index + 1} - {t.firmName}</p>
                                                    <div className="">
                                                        <p >To Organization Contact:   {infoIndividuals.filter(e => e.firmId === t.firmID).length ? infoIndividuals.filter(e => e.firmId === t.firmID).map(c => c.mail).join("; ") : "No Organization contacts"}</p>
                                                        <p>
                                                            CC Sender: {user?.individual.individualEmailAddr}
                                                        </p>
                                                    </div>
                                                </div>)
                                            : null
                                    }

                                    {
                                        isMultipleSendMail && selectOptionService && sendTo === "Service" ?
                                            mainServicesFiltered.map((t: any, index: any) => {
                                                const tempFirmId = t.firmId;
                                                const filteredServiceContacts = serviceContacts.filter(e => e.firmId === tempFirmId);
                                                const filteredFirmContacts = firmContacts.filter(e => e.firmId === tempFirmId);

                                                return (
                                                    <div className={`${index !== 0 && "mt-[20px]"}`} key={index}>
                                                        <p className="text-[1rem] mb-[5px] font-bold">
                                                            Recipient {index + 1} - {getFirmName(t.firmId)}
                                                        </p>
                                                        <div>
                                                            <p>To Service Contact: {
                                                                filteredServiceContacts.length
                                                                    ? filteredServiceContacts.map(c => c.mail).join("; ")
                                                                    : "No Service contacts"
                                                            }</p>
                                                            <p>CC Organization Contact: {
                                                                filteredFirmContacts.length
                                                                    ? filteredFirmContacts.map(c => c.mail).join("; ")
                                                                    : "No Organization contacts"
                                                            }</p>
                                                            <p>
                                                                CC Sender: {user?.individual.individualEmailAddr}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                            : null
                                    }

                                    {
                                        !isMultipleSendMail ? <div >
                                            <p className="text-[1rem] font-normal text-[#58585B] mb-[5px]">Recipient</p>
                                            <p className="">
                                                {
                                                    infoIndividuals.map((d, index, array) => {
                                                        return <span className="inline-block mr-[5px]" key={index}>
                                                            {
                                                                `${d.fullName} <${d.mail}>${!(index === array.length - 1) ? "; " : ""}`
                                                            }
                                                        </span>

                                                    })
                                                }
                                            </p>
                                        </div> : null
                                    }

                                    {
                                        !isMultipleSendMail ? <div className="mt-[20px]">
                                            <p className="font-normal mb-[5px]"><span className="text-[1rem]">CC: </span>{user && user.userConfiguration.mailCCInquiries?.map(e => e.individualEmailAddr).join("; ")}</p>
                                            <p className="font-normal mb-[5px]"><span className="text-[1rem]">CC Sender: </span>{user?.individual.individualEmailAddr}</p>
                                        </div> : null
                                    }

                                    <div className="mt-[20px]">
                                        <p className="text-[1rem] font-normal mb-[5px]">Subject <span className="text-red-700">*</span> </p>
                                        <AGInput
                                            value={values.subject}
                                            onChange={(e) => {
                                                setFieldValue("subject", e.target.value)
                                            }}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="mt-[20px]">
                                        <p className="text-[1rem] font-normal mb-[5px]">Message <span className="text-red-700">*</span></p>
                                        <AGTextarea
                                            value={values.message}
                                            onChange={(e) => {
                                                setFieldValue("message", e.target.value)
                                            }}
                                            style={{
                                                width: "100%",
                                                height: "250px",
                                                resize: "none",
                                            }}
                                        />
                                    </div>

                                    <div className="mt-[20px] flex flex-row space-x-[10px]">
                                        <AGButton
                                            type="Primary"
                                            onClick={() => { submitForm() }}
                                            className="!px-[16px] !py-[5px]"
                                            disabled={!values.subject?.trim() || !values.message?.trim()}
                                        >Send</AGButton>

                                        <AGButton
                                            type="Secondary"
                                            onClick={() => {
                                                handleClose();
                                                setSelectOptionService(undefined);
                                            }}
                                            className="!px-[16px] !py-[5px]"
                                        >Cancel</AGButton>
                                    </div>
                                </div>
                            </div>
                        </>
                    }}
                </Formik>
            </Modal ></>
    )
}

export default React.memo(RegionContactFormMessageModal);